import React from 'react';
import { Mail, Send, Save, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface EmailDraft {
  to: string;
  subject: string;
  body: string;
  timestamp?: string;
}

interface EmailDraftRendererProps {
  draft: EmailDraft;
  onSend?: () => void;
  onSave?: () => void;
  onCopy?: () => void;
  showActions?: boolean;
}

export function EmailDraftRenderer({
  draft,
  onSend,
  onSave,
  onCopy,
  showActions = true
}: EmailDraftRendererProps) {
  const handleCopy = () => {
    if (onCopy) {
      onCopy();
    } else {
      // Convert HTML to plain text for copying
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = draft.body;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      navigator.clipboard.writeText(plainText);
    }
  };

  return (
    <Card className="w-full bg-card/50 border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-primary" />
            Email Draft
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            {draft.timestamp || 'Now'}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Email Metadata */}
        <div className="space-y-2 text-sm">
          <div>
            <span className="font-medium text-muted-foreground">To:</span>{" "}
            <span className="text-foreground">{draft.to}</span>
          </div>
          <div>
            <span className="font-medium text-muted-foreground">Subject:</span>{" "}
            <span className="text-foreground">{draft.subject}</span>
          </div>
        </div>

        {/* Email Body */}
        <div className="border-t pt-3">
          <div 
            className="prose prose-sm max-w-none text-foreground"
            dangerouslySetInnerHTML={{ __html: draft.body }}
          />
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex items-center justify-between pt-3 border-t">
            <div className="flex items-center gap-2">
              {onSend && (
                <Button size="sm" onClick={onSend} className="h-8">
                  <Send className="h-3 w-3 mr-1" />
                  Send
                </Button>
              )}
              {onSave && (
                <Button size="sm" variant="outline" onClick={onSave} className="h-8">
                  <Save className="h-3 w-3 mr-1" />
                  Save Draft
                </Button>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={handleCopy} className="h-8">
              <Copy className="h-3 w-3 mr-1" />
              Copy
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}